/**
 * UserPreferencesSchema — THEME-SYSTEM-1 / SETTINGS-HUB-1a
 *
 * Zod v4 schema for the user_preferences collection.
 * Additive extension: palette + palette_set_at + mode_set_at fields
 * added by THEME-SYSTEM-1. All new fields are .optional() for backward
 * compatibility with documents created before this schema.
 */

import { z } from 'zod';

export const PALETTE_VALUES = Object.freeze(['heritage', 'platform', 'modern']);
export const MODE_VALUES     = Object.freeze(['light', 'dark', 'system']);

// VM-AEGIS-15 — VIEW-MODE-CLUSTER-RECONCILIATION-1 (2026-05-01)
// Three-state voice preference. 'click_to_speak' is the default for new
// users (privacy-respecting default — no continuous capture without explicit
// consent via the settings flow).
export const VOICE_MODE_VALUES = Object.freeze(['off', 'click_to_speak', 'always_listening']);
export const VoiceModeSchema   = z.enum(VOICE_MODE_VALUES);
export const DEFAULT_VOICE_MODE = 'click_to_speak';

export const PaletteSchema = z.enum(PALETTE_VALUES);
export const ModeSchema    = z.enum(MODE_VALUES);

// NAV-1-DENSITY-FIRST-RUN-1 — manifest soft-default contract (dd-decision-006).
// Density is now two-axis: archetype (visual family) × mode (density tier).
// `densitySeededAt` records the ISO timestamp at which a value was first
// seeded for this user (first-run from primary-module manifest, or legacy
// backfill on next session). Once set, the user's preference wins.
export const DENSITY_ARCHETYPE_VALUES = Object.freeze(['bloomberg', 'hybrid', 'linear']);
export const DENSITY_MODE_VALUES      = Object.freeze(['compact', 'comfortable', 'spacious']);
export const DensityArchetypeSchema   = z.enum(DENSITY_ARCHETYPE_VALUES);
export const DensityModeSchema        = z.enum(DENSITY_MODE_VALUES);

export const UserPreferencesSchema = z.object({
  schemaVersion: z.number().int().optional(),
  tenantId:      z.string().optional(),
  softDeleted:   z.boolean().optional(),

  profile: z.object({
    displayName:       z.string().optional(),
    avatarRef:         z.string().nullable().optional(),
    pronouns:          z.string().optional(),
    timezone:          z.string().optional(),
    locale:            z.string().optional(),
    fallbackPhoneE164: z.string().optional(),
  }).passthrough().optional(),

  interface: z.object({
    // mode — 'light' | 'dark' | 'system' (maps to data-mode; existing field)
    theme:          ModeSchema.nullable().optional(),
    // palette — THEME-SYSTEM-1 new field (maps to data-theme)
    palette:        PaletteSchema.nullable().optional(),
    palette_set_at: z.string().nullable().optional(),
    mode_set_at:    z.string().nullable().optional(),
    density:           DensityModeSchema.optional(),
    // NAV-1-DENSITY-FIRST-RUN-1: archetype + seededAt are siblings of `density`.
    // `density` is the canonical mode value (preserves SETTINGS-HUB-1a
    // SettingsToggle compatibility); the new fields are additive so legacy
    // documents remain valid until the first read backfills them.
    densityArchetype:  DensityArchetypeSchema.optional(),
    densitySeededAt:   z.string().optional(),
    viewMode:       z.string().optional(),
    reducedMotion:  z.boolean().optional(),
    sidebarCollapsed: z.boolean().optional(),
    keyboardMap:    z.string().optional(),
    landingScreen:  z.string().optional(),
    navigation: z.object({
      railLabelStyle: z.enum(['word', 'acronym']).optional(),
    }).passthrough().optional(),
  }).passthrough().optional(),

  // VM-AEGIS-15 — voice preferences. The privacy-sensitive bit is
  // `voiceMode === 'always_listening'`; UI must surface a persistent
  // "LISTENING FOR AEGIS" badge when this is on (settings UI handles the
  // explicit consent gate before flipping to always_listening).
  voice: z.object({
    voiceMode:        VoiceModeSchema.optional(),
    voiceMode_set_at: z.string().nullable().optional(),
    // Wake word is currently fixed to "aegis" (English only per spec §2.1
    // Phase 2 out-of-scope). Field reserved for future multilingual support.
    wakeWord:         z.string().optional(),
  }).passthrough().optional(),

  ai:            z.record(z.string(), z.any()).optional(),
  notifications: z.record(z.string(), z.any()).optional(),
  security:      z.record(z.string(), z.any()).optional(),
  privacy:       z.record(z.string(), z.any()).optional(),
  integrations:  z.record(z.string(), z.any()).optional(),
  meta:          z.record(z.string(), z.any()).optional(),
}).passthrough();

export const TenantThemeSettingsSchema = z.object({
  default_palette:          PaletteSchema.optional(),
  default_mode:             ModeSchema.optional(),
  default_theme_set_by:     z.string().optional(),
  default_theme_set_at:     z.string().optional(),
  // Sovereign tier opt-in (deferred to KTRUST-L1 wire-up)
  attest_all_theme_changes: z.boolean().optional(),
}).passthrough();
